import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import RevealSection from "@/components/base/RevealSection";
import { submitNetlifyForm } from "@/utils/netlifyForms";

const QUESTIONS = [
  {
    name: "q1_role",
    text: "What best describes your current role?",
    options: [
      { label: "Employee / Individual Contributor", value: "1" },
      { label: "Team Leader / Supervisor", value: "2" },
      { label: "Manager / Department Head", value: "3" },
      { label: "Business Owner / Executive", value: "4" },
    ],
  },
  {
    name: "q2_response",
    text: "When a difficult conversation is needed, what do you usually do?",
    options: [
      { label: "Avoid it as long as possible", value: "1" },
      { label: "Hint at the issue but do not address it directly", value: "2" },
      { label: "Prepare, but still feel uncomfortable", value: "3" },
      { label: "Address it directly and promptly", value: "4" },
    ],
  },
  {
    name: "q3_confidence",
    text: "How confident do you feel going into high-stakes workplace conversations?",
    options: [
      { label: "Not confident at all", value: "1" },
      { label: "Slightly confident but anxious", value: "2" },
      { label: "Moderately confident", value: "3" },
      { label: "Very confident", value: "4" },
    ],
  },
  {
    name: "q4_worry",
    text: "What typically worries you most?",
    options: [
      { label: "Conflict or confrontation", value: "1" },
      { label: "Saying the wrong thing", value: "2" },
      { label: "Damaging the relationship", value: "3" },
      { label: "Losing control of the situation", value: "4" },
    ],
  },
  {
    name: "q5_unresolved",
    text: "How often do unresolved issues build up at work?",
    options: [
      { label: "Constantly", value: "1" },
      { label: "Often", value: "2" },
      { label: "Occasionally", value: "3" },
      { label: "Rarely", value: "4" },
    ],
  },
  {
    name: "q6_emotions",
    text: "When conversations become emotional, what usually happens?",
    options: [
      { label: "I shut down or avoid", value: "1" },
      { label: "I become defensive or over-explain", value: "2" },
      { label: "I try to manage it but struggle", value: "3" },
      { label: "I stay calm and focused", value: "4" },
    ],
  },
  {
    name: "q7_expectations",
    text: "How clear are you with expectations, boundaries or feedback?",
    options: [
      { label: "Not clear — I soften or avoid", value: "1" },
      { label: "Somewhat clear but inconsistent", value: "2" },
      { label: "Mostly clear", value: "3" },
      { label: "Very clear and direct", value: "4" },
    ],
  },
  {
    name: "q8_feedback_response",
    text: "How do people typically respond to your feedback or requests?",
    options: [
      { label: "They resist, disengage or become defensive", value: "1" },
      { label: "They misunderstand or push back", value: "2" },
      { label: "They accept it, but with tension", value: "3" },
      { label: "They respond constructively", value: "4" },
    ],
  },
  {
    name: "q9_impact",
    text: "How much impact are communication challenges having?",
    options: [
      { label: "Significant — affecting stress, trust or performance", value: "1" },
      { label: "Moderate — causing ongoing frustration", value: "2" },
      { label: "Some — occasional issues", value: "3" },
      { label: "Minimal — mostly under control", value: "4" },
    ],
  },
  {
    name: "q10_improvement",
    text: "What would you most like to improve?",
    options: [
      { label: "Confidence to start", value: "1" },
      { label: "Knowing what to say", value: "2" },
      { label: "Handling emotions", value: "3" },
      { label: "Getting better outcomes", value: "4" },
    ],
  },
];

function getResultCategory(score: number): string {
  if (score <= 18) return "The Avoider";
  if (score <= 26) return "The Hesitant Communicator";
  if (score <= 34) return "The Capable But Inconsistent Communicator";
  return "The Calm Authority Builder";
}

export default function Quiz() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const step = currentStep;
  const isContactStep = step === QUESTIONS.length;
  const progress = Math.round(((step) / (QUESTIONS.length + 1)) * 100);

  const handleSelect = (value: string) => {
    setAnswers((prev) => ({ ...prev, [QUESTIONS[step].name]: value }));
    setError("");
  };

  const handleNext = () => {
    if (!answers[QUESTIONS[step].name]) {
      setError("Please select an answer to continue.");
      return;
    }
    setError("");
    setCurrentStep((s) => s + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBack = () => {
    setError("");
    setCurrentStep((s) => Math.max(0, s - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setError("");

      for (const q of QUESTIONS) {
        if (!answers[q.name]) {
          setError("Please answer all questions before submitting.");
          return;
        }
      }

      const form = e.currentTarget;
      const formData = new FormData(form);

      let totalScore = 0;
      for (const q of QUESTIONS) {
        const val = parseInt(answers[q.name], 10);
        if (!isNaN(val)) totalScore += val;
      }
      const category = getResultCategory(totalScore);

      formData.set("total_score", String(totalScore));
      formData.set("result_category", category);

      const firstName = String(formData.get("first_name") || "");
      localStorage.setItem("quiz_first_name", firstName);
      localStorage.setItem("quiz_total_score", String(totalScore));
      localStorage.setItem("quiz_result_category", category);

      setSubmitting(true);
      try {
        await submitNetlifyForm(formData);
        navigate("/quiz-results");
      } catch (err) {
        console.error(err);
        setError("Something went wrong submitting your quiz. Please try again.");
        setSubmitting(false);
      }
    },
    [answers, navigate]
  );

  return (
    <div>
      {step === 0 && (
        <RevealSection>
          <section className="bg-white px-4 md:px-8 lg:px-12 pt-16 md:pt-24 pb-8 md:pb-12">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-3xl md:text-4xl lg:text-[44px] font-bold text-navy ca-page-title mb-5">
                How Well Do You Handle Difficult Conversations at Work?
              </h1>
              <p className="text-navy/70 text-base md:text-lg lg:text-xl leading-relaxed max-w-2xl mx-auto">
                This short quiz identifies your workplace conversation style and
                what may be stopping you from handling difficult conversations
                with clarity, calm and confidence.
              </p>
            </div>
          </section>
        </RevealSection>
      )}

      <section className="bg-white px-4 md:px-8 lg:px-12 pb-6 md:pb-8 sticky top-20 md:top-28 lg:top-32 z-30">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-teal uppercase tracking-wider">
              {isContactStep ? "Your Details" : `Question ${step + 1} of ${QUESTIONS.length}`}
            </span>
            <span className="text-xs font-semibold text-teal uppercase tracking-wider">
              {progress}%
            </span>
          </div>
          <div className="w-full h-2 bg-seafoam/40 rounded-full overflow-hidden">
            <div
              className="h-full bg-teal rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </section>

      <section className="bg-seafoam/15 px-4 md:px-8 lg:px-12 py-10 md:py-16 min-h-[60vh]">
        <div className="max-w-3xl mx-auto">
          <form
            name="quiz"
            method="POST"
            data-netlify="true"
            action="/quiz-results"
            onSubmit={handleSubmit}
            className="bg-white rounded-2xl border border-seafoam/40 p-6 md:p-10 lg:p-12"
          >
            <input type="hidden" name="form-name" value="quiz" />
            <input type="hidden" name="total_score" value="" />
            <input type="hidden" name="result_category" value="" />

            {QUESTIONS.map((q) => (
              <input
                key={q.name}
                type="hidden"
                name={q.name}
                value={answers[q.name] || ""}
              />
            ))}

            {!isContactStep ? (
              <div>
                <h2 className="text-xl md:text-2xl font-semibold text-navy mb-8 leading-relaxed">
                  {QUESTIONS[step].text}
                </h2>
                <div className="flex flex-col gap-3 mb-8">
                  {QUESTIONS[step].options.map((opt) => {
                    const selected = answers[QUESTIONS[step].name] === opt.value;
                    return (
                      <label
                        key={opt.value}
                        className={`flex items-center gap-4 p-4 md:p-5 rounded-xl border-2 cursor-pointer transition-all ${
                          selected
                            ? "border-teal bg-teal/5"
                            : "border-seafoam/50 hover:border-teal/40"
                        }`}
                      >
                        <input
                          type="radio"
                          name={QUESTIONS[step].name}
                          value={opt.value}
                          checked={selected}
                          onChange={() => handleSelect(opt.value)}
                          className="w-5 h-5 accent-teal flex-shrink-0"
                        />
                        <span
                          className={`text-sm md:text-base leading-snug ${
                            selected ? "text-navy font-medium" : "text-navy/80"
                          }`}
                        >
                          {opt.label}
                        </span>
                      </label>
                    );
                  })}
                </div>

                {error && (
                  <p className="text-sm text-red-600 mb-4">{error}</p>
                )}

                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={handleBack}
                    disabled={step === 0}
                    className="inline-flex items-center text-navy/60 text-sm font-medium hover:text-navy transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <i className="ri-arrow-left-line mr-1" />
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={handleNext}
                    className="inline-flex items-center bg-teal text-white font-semibold px-8 py-3 rounded-lg hover:bg-navy transition-all text-sm whitespace-nowrap"
                  >
                    {step === QUESTIONS.length - 1 ? "Continue" : "Next"}
                    <i className="ri-arrow-right-line ml-2" />
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <h2 className="text-xl md:text-2xl font-semibold text-navy mb-2">
                  Almost there
                </h2>
                <p className="text-navy/60 text-base mb-8">
                  Enter your details to receive your personalised results.
                </p>

                <div className="flex flex-col gap-5 mb-8">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="flex flex-col gap-2">
                      <label htmlFor="first_name" className="text-sm font-medium text-navy">
                        First Name
                      </label>
                      <input
                        id="first_name"
                        name="first_name"
                        type="text"
                        required
                        className="w-full border border-seafoam rounded-lg px-4 py-3 text-navy text-base focus:outline-none focus:ring-2 focus:ring-teal focus:border-transparent transition-all"
                        placeholder="Jane"
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label htmlFor="last_name" className="text-sm font-medium text-navy">
                        Last Name
                      </label>
                      <input
                        id="last_name"
                        name="last_name"
                        type="text"
                        required
                        className="w-full border border-seafoam rounded-lg px-4 py-3 text-navy text-base focus:outline-none focus:ring-2 focus:ring-teal focus:border-transparent transition-all"
                        placeholder="Doe"
                      />
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label htmlFor="email" className="text-sm font-medium text-navy">
                      Email
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      className="w-full border border-seafoam rounded-lg px-4 py-3 text-navy text-base focus:outline-none focus:ring-2 focus:ring-teal focus:border-transparent transition-all"
                      placeholder="jane@example.com"
                    />
                  </div>
                </div>

                {error && (
                  <p className="text-sm text-red-600 mb-4">{error}</p>
                )}

                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={handleBack}
                    className="inline-flex items-center text-navy/60 text-sm font-medium hover:text-navy transition-colors"
                  >
                    <i className="ri-arrow-left-line mr-1" />
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex items-center bg-teal text-white font-semibold px-8 py-3 rounded-lg hover:bg-navy transition-all text-sm whitespace-nowrap disabled:opacity-60"
                  >
                    {submitting ? "Submitting..." : "Get My Results"}
                    <i className="ri-arrow-right-line ml-2" />
                  </button>
                </div>
              </div>
            )}
          </form>
        </div>
      </section>
    </div>
  );
}
